import { DateTime } from "luxon";
import { type ZodString, z } from "zod";

const normalizeOptionalValue = <T>(value: T | null | undefined): T | undefined => {
	return value ?? undefined;
};

export const isMidnightEndSchedulePeriod = (start: Date, end: Date, siteTimezone: string): boolean => {
	const startDate = DateTime.fromJSDate(start, { zone: "UTC" }).setZone(siteTimezone);
	const endDate = DateTime.fromJSDate(end, { zone: "UTC" }).setZone(siteTimezone);

	return (
		startDate.isValid &&
		endDate.isValid &&
		endDate.hour === 0 &&
		endDate.minute === 0 &&
		endDate.second === 0 &&
		endDate.millisecond === 0 &&
		endDate.hasSame(startDate.plus({ days: 1 }), "day")
	);
};

const createProductionScheduleSchema = (siteTimezone?: string) => {
	const scheduleSchema = z
		.object({
			id: z.string().optional(),
			tenantId: z.string().optional(),
			resourceId: z.string().min(1, "Machine is required"),
			resourceType: z.string().nullish().transform(normalizeOptionalValue).optional(),
			title: z.string().transform((value) => (value === "" ? "Planned Production" : value)),
			label: z.string().nullish().transform(normalizeOptionalValue).optional(),
			color: z.preprocess<unknown, ZodString, string>((value) => (!value ? "" : value), z.string()),
			start: z.coerce.date<Date>({ error: "Invalid input" }),
			end: z.coerce.date<Date>({ error: "Invalid input" }),
			recurrenceEndDate: z.coerce
				.date<Date>({ error: "Invalid input" })
				.nullish()
				.transform(normalizeOptionalValue)
				.optional(),
			downtime: z.boolean().optional(),
			excludedLocalDates: z.array(z.string()).optional(),

			repeatSunday: z.boolean(),
			repeatMonday: z.boolean(),
			repeatTuesday: z.boolean(),
			repeatWednesday: z.boolean(),
			repeatThursday: z.boolean(),
			repeatFriday: z.boolean(),
			repeatSaturday: z.boolean(),

			duration: z
				.object({
					milliseconds: z.number(),
				})
				.optional(),
		})
		.refine((data) => data.end > data.start, {
			message: "End time must be after start time",
			path: ["end"],
		})
		.refine(
			(data) => {
				if (siteTimezone === undefined) {
					return true;
				}

				const startDate = DateTime.fromJSDate(data.start, { zone: "UTC" }).setZone(siteTimezone);
				const endDate = DateTime.fromJSDate(data.end, { zone: "UTC" }).setZone(siteTimezone);

				return startDate.hasSame(endDate, "day") || isMidnightEndSchedulePeriod(data.start, data.end, siteTimezone);
			},
			{
				message: "Overnight schedules are not supported",
				path: ["end"],
			},
		)
		.refine(
			(data) => {
				if (siteTimezone === undefined || data.recurrenceEndDate === undefined) {
					return true;
				}

				const startDate = DateTime.fromJSDate(data.start, { zone: "UTC" }).setZone(siteTimezone).startOf("day");
				const recurrenceEndDate = DateTime.fromJSDate(data.recurrenceEndDate, { zone: "UTC" })
					.setZone(siteTimezone)
					.startOf("day");

				return recurrenceEndDate >= startDate;
			},
			{
				message: "Recurrence end date must be on or after the start date",
				path: ["recurrenceEndDate"],
			},
		);

	return scheduleSchema;
};

export const productionScheduleSchema = createProductionScheduleSchema();
export const productionScheduleApiSchema = createProductionScheduleSchema();
export const productionScheduleSchemaForTimezone = (
	siteTimezone: string,
): ReturnType<typeof createProductionScheduleSchema> => {
	return createProductionScheduleSchema(siteTimezone);
};

export const isProductionScheduleTimeAllowed = (time: DateTime, position: "start" | "end"): boolean => {
	if (time.isValid === false || time.second !== 0 || time.millisecond !== 0) {
		return false;
	}

	return time.minute % 15 === 0 || (position === "end" && time.hour === 23 && time.minute === 59);
};

export const productionScheduleFormSchemaForTimezone = (
	siteTimezone: string,
): ReturnType<typeof createProductionScheduleSchema> => {
	return productionScheduleSchemaForTimezone(siteTimezone)
		.refine(
			(data) => {
				return isProductionScheduleTimeAllowed(DateTime.fromJSDate(data.start, { zone: siteTimezone }), "start");
			},
			{ message: "Start time must be in 15-minute increments", path: ["start"] },
		)
		.refine(
			(data) => {
				return isProductionScheduleTimeAllowed(DateTime.fromJSDate(data.end, { zone: siteTimezone }), "end");
			},
			{ message: "End time must be in 15-minute increments or 11:59 PM", path: ["end"] },
		);
};

type ProductionScheduleSchemaOutput = z.output<typeof productionScheduleSchema>;

export type ProductionSchedule = Omit<
	ProductionScheduleSchemaOutput,
	"resourceType" | "label" | "recurrenceEndDate"
> & {
	resourceType?: string;
	label?: string;
	recurrenceEndDate?: Date;
};
export type ProductionScheduleInput = z.input<typeof productionScheduleSchema>;
